import { DeviceToolError } from "../errors";
import type { DeviceInfo, Protocol, UsbProtocolService } from "../types";
import { matchProtocolInterface, usbVersion, webUsbFilters, type InterfaceMatch } from "./UsbDeviceMatcher";

export interface TransferOptions {
  timeoutMs?: number;
  signal?: AbortSignal;
}

export interface ConnectOptions {
  selectionOnly?: (device: USBDevice) => boolean;
}

function raceWithTimeout<T>(promise: Promise<T>, options: TransferOptions): Promise<T> {
  const timeoutMs = options.timeoutMs ?? 10_000;
  return new Promise<T>((resolve, reject) => {
    let settled = false;
    const timer = window.setTimeout(() => {
      if (!settled) reject(new DeviceToolError("TRANSFER_TIMEOUT"));
    }, timeoutMs);
    const abort = () => reject(new DeviceToolError("OPERATION_CANCELLED"));
    if (options.signal?.aborted) return abort();
    options.signal?.addEventListener("abort", abort, { once: true });
    promise.then(
      (value) => {
        settled = true;
        window.clearTimeout(timer);
        options.signal?.removeEventListener("abort", abort);
        resolve(value);
      },
      (error) => {
        settled = true;
        window.clearTimeout(timer);
        options.signal?.removeEventListener("abort", abort);
        reject(error);
      },
    );
  });
}

function isUsbAccessDenied(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error);
  return (typeof DOMException !== "undefined" && error instanceof DOMException && ["SecurityError", "NotAllowedError"].includes(error.name)) || /access denied|permission denied|insufficient permission/i.test(message);
}

export class WebUsbTransport implements UsbProtocolService {
  private device?: USBDevice;
  private match?: InterfaceMatch;
  private protocol: Protocol;
  private connected = false;
  private operationController?: AbortController;

  constructor(protocol: Protocol) {
    this.protocol = protocol;
  }

  get usbDevice(): USBDevice | undefined {
    return this.device;
  }

  get interfaceMatch(): InterfaceMatch | undefined {
    return this.match;
  }

  async connect(options: ConnectOptions = {}): Promise<void> {
    this.assertEnvironment();
    const usb = navigator.usb;
    if (!usb) throw new DeviceToolError("WEBUSB_UNSUPPORTED");
    const device = await usb.requestDevice({ filters: webUsbFilters(this.protocol) });
    await this.connectSelectedDevice(device, options);
  }

  /**
   * Opens a device that the browser has already authorized. This is used by
   * the USB connect event and getDevices() auto-discovery; it never opens the
   * permission picker, which browsers only allow from a user gesture.
   */
  async connectAuthorizedDevice(device: USBDevice, options: ConnectOptions = {}): Promise<void> {
    this.assertEnvironment();
    await this.connectSelectedDevice(device, options);
  }

  private assertEnvironment(): void {
    if (!navigator.usb) throw new DeviceToolError("WEBUSB_UNSUPPORTED");
    if (!window.isSecureContext && location.hostname !== "localhost" && location.hostname !== "127.0.0.1") {
      throw new DeviceToolError("INSECURE_CONTEXT");
    }
  }

  private async connectSelectedDevice(device: USBDevice, options: ConnectOptions): Promise<void> {
    if (this.device || this.connected) await this.disconnect();
    const match = matchProtocolInterface(device, this.protocol);
    if (options.selectionOnly?.(device)) {
      this.device = device;
      this.match = match;
      this.connected = false;
      return;
    }
    if (!match) throw new DeviceToolError("INTERFACE_NOT_FOUND");
    try {
      await device.open();
      if (!device.configuration) {
        const configuration = device.configurations[0];
        if (!configuration) throw new DeviceToolError("INTERFACE_NOT_FOUND", "Thiết bị không có configuration.");
        await device.selectConfiguration(configuration.configurationValue);
      }
      await device.claimInterface(match.interfaceNumber);
    } catch (error) {
      await device.close().catch(() => undefined);
      if (error instanceof DeviceToolError) throw error;
      if (isUsbAccessDenied(error)) {
        const usbId = `VID 0x${device.vendorId.toString(16).padStart(4, "0")} / PID 0x${device.productId.toString(16).padStart(4, "0")} / interface ${match.interfaceNumber}`;
        const driverGuidance = device.vendorId === 0x1b8e && device.productId === 0xc003
          ? " PID 0xC003 đang dùng libusb-win32/WorldCup Driver; driver này không cho Chrome WebUSB mở thiết bị. Nếu muốn dùng trình duyệt, thay đúng thiết bị này sang WinUSB trong Zadig. Nếu muốn dùng USB Burning Tool v2, giữ WorldCup Driver và không kết nối bằng WebUSB."
          : " Hãy đóng Amlogic USB Burning Tool v2, Zadig và mọi ứng dụng USB khác; trên Windows hãy gán driver WinUSB cho đúng interface rồi khởi động lại Chrome/Edge.";
        throw new DeviceToolError(
          "USB_ACCESS_DENIED",
          `${usbId}.${driverGuidance} Nếu tiếp tục dùng tool native, khôi phục lại driver Amlogic sau khi thử nghiệm.`,
          undefined,
          error,
        );
      }
      throw new DeviceToolError("CLAIM_INTERFACE_FAILED", error instanceof Error ? error.message : String(error), undefined, error);
    }
    this.device = device;
    this.match = match;
    this.connected = true;
  }

  async disconnect(): Promise<void> {
    this.operationController?.abort();
    const device = this.device;
    const match = this.match;
    const wasConnected = this.connected;
    this.connected = false;
    this.device = undefined;
    this.match = undefined;
    if (!device) return;
    if (wasConnected && match && device.opened) await device.releaseInterface(match.interfaceNumber).catch(() => undefined);
    if (device.opened) await device.close().catch(() => undefined);
  }

  isConnected(): boolean {
    return this.connected && Boolean(this.device?.opened);
  }

  async getDeviceInfo(): Promise<DeviceInfo> {
    if (!this.device || !this.match) throw new DeviceToolError("DEVICE_NOT_SELECTED");
    return {
      protocol: this.protocol,
      vendorId: this.device.vendorId,
      productId: this.device.productId,
      manufacturer: this.device.manufacturerName,
      product: this.device.productName,
      serial: this.device.serialNumber,
      usbVersion: usbVersion(this.device),
      interfaceNumber: this.match.interfaceNumber,
      endpointIn: this.match.endpointIn?.endpointNumber,
      endpointOut: this.match.endpointOut?.endpointNumber,
      interfaceClaimed: true,
    };
  }

  async transferOut(data: Uint8Array, options: TransferOptions = {}): Promise<number> {
    if (!this.device || !this.match?.endpointOut || !this.connected) throw new DeviceToolError("DEVICE_DISCONNECTED");
    const controller = new AbortController();
    this.operationController = controller;
    const signal = options.signal ?? controller.signal;
    try {
      const result = await raceWithTimeout(this.device.transferOut(this.match.endpointOut.endpointNumber, data), { ...options, signal });
      if (result.status === "stall") {
        await this.device.clearHalt("out", this.match.endpointOut.endpointNumber).catch(() => undefined);
        throw new DeviceToolError("TRANSFER_STALL");
      }
      return result.bytesWritten;
    } catch (error) {
      if (error instanceof DeviceToolError) throw error;
      throw new DeviceToolError("TRANSFER_STALL", error instanceof Error ? error.message : String(error), undefined, error);
    } finally {
      this.operationController = undefined;
    }
  }

  async transferIn(length: number, options: TransferOptions = {}): Promise<Uint8Array> {
    if (!this.device || !this.match?.endpointIn || !this.connected) throw new DeviceToolError("DEVICE_DISCONNECTED");
    const controller = new AbortController();
    this.operationController = controller;
    const signal = options.signal ?? controller.signal;
    try {
      const result = await raceWithTimeout(this.device.transferIn(this.match.endpointIn.endpointNumber, length), { ...options, signal });
      if (result.status === "stall") {
        await this.device.clearHalt("in", this.match.endpointIn.endpointNumber).catch(() => undefined);
        throw new DeviceToolError("TRANSFER_STALL");
      }
      return result.data ? new Uint8Array(result.data.buffer, result.data.byteOffset, result.data.byteLength) : new Uint8Array();
    } catch (error) {
      if (error instanceof DeviceToolError) throw error;
      throw new DeviceToolError("TRANSFER_STALL", error instanceof Error ? error.message : String(error), undefined, error);
    } finally {
      this.operationController = undefined;
    }
  }

  async cancelCurrentOperation(): Promise<void> {
    this.operationController?.abort();
  }
}
